import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Pencil, X } from 'lucide-react';
import Modal from 'components/modal';
import { LanguageToggle } from '../LanguageToggle/LanguageToggle';
import { cacheUserMetadata, CheckAuth, getUserDetails } from 'auth/auth';
import { useUpdateMyProfileMutation } from 'redux_store/services/admin/adminEmployee';
import { PROVINCES } from 'constants/geographic';
import styles from '../../styles/components/profile-section-modal.module.css';

/**
 * Profile section modal opened from the left-rail profile row (PROD-796).
 *
 * MS Code (PROD-256) and the MS province (PROD-256/796, labelled "MS Location"
 * per the design) both save through the
 * self-service `PUT b2b/users/me/profile` endpoint. MS Code is required, so an
 * empty value blocks the save; the province is optional and clearing it sends
 * null. Preferred Language reuses the shared LanguageToggle, which already
 * persists the choice per role.
 *
 * Notifications is rendered per the design but intentionally NOT persisted —
 * there is no per-user notifications-preference endpoint yet, so it resets when
 * the modal reopens.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible.
 * @param {function} props.onClose - Called when the modal should close.
 * @param {string} props.displayName - Full name shown in the header.
 * @param {string} props.displayEmail - Email shown in the header.
 * @param {string} props.initials - Avatar initials.
 * @param {boolean} props.isBorrower - When true (consumer/borrower profile),
 *   the MS Code and MS Location fields are hidden — they are mortgage-specialist
 *   fields that do not apply to borrowers (and MS Code is otherwise required,
 *   which would block the borrower's save).
 * @returns {JSX.Element}
 */
const ProfileSectionModal = ({
  show,
  onClose,
  displayName,
  displayEmail,
  initials,
  isBorrower = false,
}) => {
  const { t } = useTranslation(['navigation', 'common']);
  const [updateMyProfile, { isLoading: saving }] = useUpdateMyProfileMutation();

  const [msCode, setMsCode] = useState('');
  const [province, setProvince] = useState('');
  const [notificationsOn, setNotificationsOn] = useState(true);
  const [error, setError] = useState('');

  // Seed from the cached profile every time the modal opens so a cancelled
  // edit never leaks into the next one.
  useEffect(() => {
    if (!show) return;
    const details = getUserDetails();
    setMsCode(details?.msCode || '');
    setProvince(details?.province || '');
    setNotificationsOn(true);
    setError('');
  }, [show]);

  const handleSave = async () => {
    // Borrowers have no MS fields in this modal, so there is nothing to persist
    // through the profile endpoint (preferred language saves itself via the
    // toggle). Just close.
    if (isBorrower) {
      onClose?.();
      return;
    }
    const value = (msCode || '').trim();
    if (!value) {
      setError(t('navigation:profileModal.msCodeRequired'));
      return;
    }
    const nextProvince = province || null;
    try {
      const res = await updateMyProfile({
        ms_code: value,
        province: nextProvince,
      }).unwrap();
      const savedCode = res?.ms_code ?? res?.msCode ?? value;
      const savedProvince = res?.province ?? nextProvince;
      // Keep the whole cached record and only swap the MS fields —
      // cacheUserMetadata rebuilds role/permissions from what it is handed, so
      // a partial object would wipe them.
      cacheUserMetadata({
        ...(CheckAuth() || {}),
        ms_code: savedCode,
        province: savedProvince,
      });
      setMsCode(savedCode);
      setProvince(savedProvince || '');
      setError('');
      onClose?.();
    } catch (err) {
      const detail = err?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : t('navigation:profileModal.msCodeSaveFailed')
      );
    }
  };

  if (!show) return null;

  return (
    // Modal's wrapper adds 16px of padding, so the card renders at the
    // Figma width of 420px.
    <Modal show={show} onClose={onClose} width="max-w-[452px]">
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.avatar}>
            {initials || '?'}
            <span className={styles.avatarBadge} aria-hidden="true">
              <Pencil size={8} strokeWidth={2.5} />
            </span>
          </div>

          <div className={styles.identity}>
            <p className={styles.identityName}>{displayName}</p>
            <p className={styles.identityEmail}>{displayEmail}</p>
          </div>

          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common:buttons.close')}
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </div>

        <div className={styles.divider} />

        <div className={styles.body}>
          <div className={styles.inlineRow}>
            <span className={styles.label}>
              {t('navigation:profileModal.preferredLanguage')}
            </span>
            <LanguageToggle variant="default" />
          </div>

          <div className={styles.divider} />

          <div className={styles.inlineRow}>
            <span className={styles.label} id="profile-notifications-label">
              {t('navigation:notifications')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={notificationsOn}
              aria-labelledby="profile-notifications-label"
              onClick={() => setNotificationsOn((on) => !on)}
              className={`${styles.toggle} ${
                notificationsOn ? styles.toggleOn : ''
              }`}
            >
              <span className={styles.toggleKnob} />
            </button>
          </div>

          {/* MS Code + MS Location are mortgage-specialist fields; hidden on
              the borrower profile (MS Code is otherwise required and would
              block a borrower's save). */}
          {!isBorrower && (
            <>
              <div className={styles.divider} />

              <div className={styles.fieldRow}>
                <label className={styles.label} htmlFor="profile-ms-code">
                  {t('navigation:profileModal.msCode')}
                </label>
                <input
                  id="profile-ms-code"
                  type="text"
                  value={msCode}
                  maxLength={50}
                  autoFocus
                  required
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'profile-ms-code-error' : undefined}
                  placeholder={t('navigation:profileModal.msCodePlaceholder')}
                  className={`${styles.input} ${error ? styles.inputError : ''}`}
                  onChange={(e) => {
                    setMsCode(e.target.value);
                    if (error) setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                  }}
                />
                {error && (
                  <p className={styles.errorText} id="profile-ms-code-error">
                    {error}
                  </p>
                )}
              </div>

              <div className={styles.divider} />

              {/* MS Location: Canadian provinces/territories only. Stored as the
                  two-letter code on users.province; optional, so the blank
                  option clears it. */}
              <div className={styles.fieldRow}>
                <label className={styles.label} htmlFor="profile-ms-location">
                  {t('navigation:profileModal.msLocation')}
                </label>
                <div className={styles.controlShell}>
                  <select
                    id="profile-ms-location"
                    className={styles.select}
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                  >
                    <option value="">
                      {t('navigation:profileModal.msLocationPlaceholder')}
                    </option>
                    {PROVINCES.map((province) => (
                      <option key={province.code} value={province.code}>
                        {province.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    strokeWidth={2.5}
                    className={styles.selectChevron}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.divider} />

        <div className={styles.footer}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.cancelButton}`}
            onClick={onClose}
            disabled={saving}
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.saveButton}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              t('common:buttons.save')
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ProfileSectionModal;
